import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { blurbDefault, stagger, galleryPhotos } from '../utils/quinn';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { Lightbox, useLightbox } from '../utils/lightbox';

interface Photo {
  image?: string;
  alt_text?: string;
  title?: string;
}

const card1Def: SlotGroupDefinition = {
  label: 'Highlight 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
    label: { label: 'Label', type: 'text', defaultField: 'photo_gallery[0].title', defaultValue: 'Pool' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text', label: 'title' } },
  generator: {
    prompt: 'The strongest shared community space this property has a real photo of, with a one- or two-word label naming the space.',
    context: 'Card 1 of 6 in the Amenity Highlights photo row on the Amenities page (card thumb). Do not reuse a photo or space from cards 2–6.',
  },
};

const card2Def: SlotGroupDefinition = {
  label: 'Highlight 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
    label: { label: 'Label', type: 'text', defaultField: 'photo_gallery[1].title', defaultValue: 'Playground' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text', label: 'title' } },
  generator: {
    prompt: 'Another shared space or outdoor feature at this property with its own photo, labelled in one or two words.',
    context: 'Card 2 of 6 in the Amenity Highlights photo row (card thumb). Different space and photo from cards 1 and 3–6.',
  },
};

const card3Def: SlotGroupDefinition = {
  label: 'Highlight 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
    label: { label: 'Label', type: 'text', defaultField: 'photo_gallery[2].title', defaultValue: 'Clubhouse' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text', label: 'title' } },
  generator: {
    prompt: 'A third distinct amenity or community space shown in the gallery, labelled in one or two words.',
    context: 'Card 3 of 6 in the Amenity Highlights photo row (card thumb). Different space and photo from the other cards.',
  },
};

const card4Def: SlotGroupDefinition = {
  label: 'Highlight 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
    label: { label: 'Label', type: 'text', defaultField: 'photo_gallery[3].title', defaultValue: 'Fire pit' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text', label: 'title' } },
  generator: {
    prompt: 'A fourth distinct amenity with a real photo, or leave empty if the gallery has no further amenity photos.',
    context: 'Card 4 of 6 in the Amenity Highlights photo row (card thumb). Optional; never repeat a space from cards 1–3, 5 or 6.',
  },
};

const card5Def: SlotGroupDefinition = {
  label: 'Highlight 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
    label: { label: 'Label', type: 'text', defaultField: 'photo_gallery[4].title', defaultValue: 'Courtyard' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 4, fieldMappings: { image: 'image', alt: 'alt_text', label: 'title' } },
  generator: {
    prompt: 'A fifth distinct amenity with a real photo, or leave empty if none is left.',
    context: 'Card 5 of 6 in the Amenity Highlights photo row (card thumb). Optional; different from every other card.',
  },
};

const card6Def: SlotGroupDefinition = {
  label: 'Highlight 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
    label: { label: 'Label', type: 'text', defaultField: 'photo_gallery[5].title', defaultValue: 'Garages' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', alt: 'alt_text', label: 'title' } },
  generator: {
    prompt: 'A sixth distinct amenity with a real photo, or leave empty if none is left.',
    context: 'Card 6 of 6 in the Amenity Highlights photo row (card thumb). Optional; different from every other card.',
  },
};

/** Keyword order for the fallback pick: one gallery photo per keyword, matched on alt text. */
const KEYWORDS: Array<[string, RegExp]> = [
  ['Pool', /\bpool\b/i],
  ['Dog park', /dog park|dog run|pet park|paw park/i],
  ['Playground', /playground|play area|tot lot/i],
  ['Clubhouse', /clubhouse|club house|lounge|resident center/i],
  ['Outdoor kitchen', /outdoor kitchen|grill|bbq|barbecue/i],
  ['Fire pit', /fire ?pit/i],
  ['EV charging', /\bev\b|charging/i],
  ['Coffee bar', /coffee bar|coffee station|café/i],
  ['Fitness', /fitness|gym|workout/i],
  ['Trails', /\btrail/i],
  ['Courtyard', /courtyard|lawn/i],
  ['Garages', /garage/i],
];

function keywordPicks(photos: Photo[]): Array<{ label: string; g: Photo }> {
  const used = new Set<Photo>();
  const out: Array<{ label: string; g: Photo }> = [];
  for (const [label, rx] of KEYWORDS) {
    const g = photos.find((x) => !used.has(x) && rx.test(x.alt_text || ''));
    if (g) {
      used.add(g);
      out.push({ label, g });
    }
    if (out.length === 6) break;
  }
  return out;
}

export function AmenityHighlights() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const lb = useLightbox();
  const slots = [
    useSlotGroup('amenityhighlights.card_1', card1Def),
    useSlotGroup('amenityhighlights.card_2', card2Def),
    useSlotGroup('amenityhighlights.card_3', card3Def),
    useSlotGroup('amenityhighlights.card_4', card4Def),
    useSlotGroup('amenityhighlights.card_5', card5Def),
    useSlotGroup('amenityhighlights.card_6', card6Def),
  ];

  const photos = galleryPhotos<Photo>(p.photo_gallery);
  const picks = keywordPicks(photos);
  const cards = slots.map((s, i) => {
    const overridden = !!s.image && s.image !== photos[i]?.image;
    if (overridden) return { src: s.image!, alt: s.alt || '', label: s.label || '' };
    const k = picks[i];
    return k ? { src: k.g.image!, alt: k.g.alt_text || '', label: k.label } : null;
  });
  const shown = cards.filter(Boolean).length;

  const blurb = p.property_amenities_blurb;
  const leadDefault = blurbDefault(blurb, 'body', { paragraph: 1 });
  const lbItems = cards.filter((c): c is NonNullable<typeof c> => !!c).map((c) => ({ src: c.src, alt: c.alt, caption: c.label }));

  if (shown < 3 && !editing) return null;

  const cardBody = (i: number) => {
    const c = cards[i];
    if (!c)
      return editing ? (
        <div className="relative flex h-[280px] items-center justify-center overflow-hidden rounded-md border border-dashed border-[--q-border] bg-[--q-surface] text-sm text-[--q-muted] max-[767px]:h-[200px] max-[767px]:w-[280px] max-[767px]:shrink-0 max-[767px]:snap-start">
          Highlight {i + 1}
        </div>
      ) : null;
    return (
      <a
        className="relative block h-[280px] overflow-hidden rounded-md bg-[--q-surface] [&_img]:absolute [&_img]:inset-0 [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_img]:[transition:transform_var(--q-motion-slow)_var(--q-ease-out)] [&:focus-visible_img]:[transform:scale(1.04)] [&:hover_img]:[transform:scale(1.04)] max-[767px]:h-[200px] max-[767px]:w-[280px] max-[767px]:shrink-0 max-[767px]:snap-start"
        href="#"
        data-reveal
        style={stagger(i)}
        onClick={(e) => {
          e.preventDefault();
          lb.open(lbItems.findIndex((x) => x.src === c.src));
        }}
      >
        <VibesitesImage url={c.src} alt={c.alt} sizes="(min-width: 1200px) 33vw, (min-width: 768px) 50vw, 280px" />
      </a>
    );
  };

  return (
    <section className="section [&.section]:pt-6 [&_.section\_\_head]:items-start max-[767px]:[&.section]:pt-4">
      <div className="section__head" data-reveal>
        <div className="section__lead-block">
          <RichTextContent path="amenityhighlights.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="amenityhighlights.title" as="h2" className="h2" />
        </div>
        <RichTextContent path="amenityhighlights.lead" as="p" className="section__lead" defaultValue={leadDefault} />
      </div>
      <div className="grid grid-cols-[repeat(3,1fr)] gap-6 [&>div:empty]:hidden [&>div]:min-w-0 max-[1199px]:grid-cols-[repeat(2,1fr)] max-[767px]:flex max-[767px]:items-start max-[767px]:gap-3 max-[767px]:overflow-x-auto max-[767px]:overflow-y-hidden max-[767px]:overscroll-x-contain max-[767px]:[touch-action:pan-x_pan-y] max-[767px]:px-[--gutter] max-[767px]:py-0 max-[767px]:[margin:0_calc(-1*var(--gutter))] max-[767px]:[scroll-padding:0_var(--gutter)] max-[767px]:[scroll-snap-type:x_mandatory] max-[767px]:[scrollbar-width:none] max-[767px]:[&::-webkit-scrollbar]:hidden max-[767px]:[&>div]:shrink-0">
        <EditableSlotGroup groupPath="amenityhighlights.card_1" definition={card1Def}>
          {cardBody(0)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="amenityhighlights.card_2" definition={card2Def}>
          {cardBody(1)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="amenityhighlights.card_3" definition={card3Def}>
          {cardBody(2)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="amenityhighlights.card_4" definition={card4Def}>
          {cardBody(3)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="amenityhighlights.card_5" definition={card5Def}>
          {cardBody(4)}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="amenityhighlights.card_6" definition={card6Def}>
          {cardBody(5)}
        </EditableSlotGroup>
      </div>
      <RichTextContent path="amenityhighlights.hint" as="div" className="hidden max-[767px]:mt-4 max-[767px]:block max-[767px]:text-[12px] max-[767px]:text-[--q-muted]" />
      <Lightbox items={lbItems} index={lb.index} onClose={lb.close} onIndex={lb.setIndex} />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenityHighlights',
  displayName: 'Amenity Highlights',
  tokens: {
    'amenityhighlights.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Around the community</p>' },
    'amenityhighlights.title': { label: 'Title', type: 'richtext', defaultValue: "<p>The places you'll use every week</p>" },
    'amenityhighlights.lead': {
      label: 'Lead',
      description: 'Until edited, shows part of the blurb (its bold first sentence for a title, the rest for a lead). Pick the blurb field from the suggestions to show it in full.',
      type: 'richtext',
      defaultValue: '',
      generator: {
        prompt: 'A lead about the shared spaces residents use week to week at this property. Name only spaces it really has.',
        context: 'Lead of the Amenity Highlights photo row near the top of the Amenities page, right under the page hero.',
        length: '1–2 sentences',
      },
    },
    'amenityhighlights.hint': { label: 'Swipe Hint (phone)', type: 'richtext', defaultValue: '<p>Swipe for more</p>' },
  },
  slotGroups: {
    'amenityhighlights.card_1': card1Def,
    'amenityhighlights.card_2': card2Def,
    'amenityhighlights.card_3': card3Def,
    'amenityhighlights.card_4': card4Def,
    'amenityhighlights.card_5': card5Def,
    'amenityhighlights.card_6': card6Def,
  },
};

(AmenityHighlights as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenityHighlights;
